import BaseModal from "./BaseModal";
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import { TextInputContainer } from "../forms/TextInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Switch } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import SessionTypeSelect from "../forms/SessionTypeSelect";
import { useTranslation } from "react-i18next";
import GenderSelect from "../GenderSelect";
import { isEmpty } from "../../services/helper";
import { RepeatType } from "services/repeatType";
import generateRepetitionPreview, { applyRepeatTypeSelection, checkForRepeatErrors } from "services/repetitionHelper";
import { useRankingFilterNormalizer, useRankingFilterValidator } from "services/RankingFilterHelper";
import Info from "components/Info";

const useStyles = makeStyles((theme) => ({
    radioLabel: {
        color: theme.palette.primary.light,
        display: 'flex',
        alignContent: "space-between",
    },
    previewTitle: {
        color: theme.palette.primary.light,
        height: "2.5rem",
        "white-space": "pre-line",
    },
    preview: {
        color: theme.palette.primary.light,
        "white-space": "pre-line",
        maxHeight: "15vh",
        overflow: "auto",
        "&::-webkit-scrollbar": {
            width: 5,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.secondary.main,
            borderRadius: "10px"
        },
    },
    root: {
        '& label': {
            color: theme.palette.primary.light,
            fontStyle: "italic",
            '&.MuiInputLabel-shrink': {
                fontStyle: "normal"
            },
            '&.Mui-focused': {
                color: theme.palette.secondary.main,
            },
            '&:hover': {
                color: theme.palette.secondary.main,
            },
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& fieldset': {
                borderColor: theme.palette.primary.light,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.secondary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.secondary.main,
                borderWidth: "3px"
            },
        },
        "& .Mui-error": {
            color: theme.palette.error.light,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.error.light,
            }
        },
    },
    input: {
        color: theme.palette.primary.light,
        '&:focused': {
            borderColor: theme.palette.secondary.main,
        },
        '&:hover': {
            color: theme.palette.secondary.main,
        },
        '&::placeholder': { /* Chrome, Firefox, Opera, Safari 10.1+ */
            color: theme.palette.secondary.main,
            opacity: 1 /* Firefox */
        },
        '&:-ms-input-placeholder': { /* Internet Explorer 10-11 */
            color: theme.palette.secondary.main,
        },
        '&::-ms-input-placeholder': { /* Microsoft Edge */
            color: theme.palette.secondary.main,
        }
    }
}));

const buildConvertedValues = (values) => {
  let converted = { 
    ...values,
    startDate: Date.parse(values.startDate),
    endDate: Date.parse(values.endDate),
  };

  if (converted.repeatType === RepeatType.MONTH) {
    const startMonthJs = dayjs(converted.startMonth).utc(false);
    converted.startDate = startMonthJs.startOf("month").valueOf();
    converted.endDate = startMonthJs.endOf("month").valueOf();
    delete converted.startMonth;
  }

  return converted;
};

const validateRankFields = (values) => {
  if (!values.title) return { field: "title", error: "mandatoryField" };
  if (values.value == null) return { field: "value", error: "mandatoryField" };
  if (values.repeatType !== RepeatType.NONE && values.isAwardLimit && isEmpty(values.awardsLimitForGroup))
    return { field: "awardsLimitForGroup", error: "mandatoryField" };
  return null;
};

export default function CreateRankModal({ open, onClose, onSubmit, usefulValues, rankingFilter, customField }) {

    const { t } = useTranslation();
    let [values, setValues] = useState({ repeatType: RepeatType.NONE });
    let [error, setError] = useState({});
    let classes = useStyles();
    const rankPreview = React.useMemo(() => generateRepetitionPreview(t, values), [t, values]);
    const rankingFilterValidatorFunction = useRankingFilterValidator();
    const rankingFilterNormalizerFunction = useRankingFilterNormalizer();

    const save = () => {
        let converted = buildConvertedValues(values);

        const filterErr = rankingFilterValidatorFunction(converted);
        if (filterErr) return setError({ [filterErr.field]: t(filterErr.error) });

        const repeatErr = checkForRepeatErrors(values);
        if (repeatErr) return setError({ [repeatErr.field]: t(repeatErr.error) });

        const rankingErr = validateRankFields(converted);
        if (rankingErr) return setError({ [rankingErr.field]: t(rankingErr.error) });

        if (converted.filter === '') delete converted.filter;

        converted = rankingFilterNormalizerFunction(converted);

        onSubmit(converted);
        close();
    };

    const close = () => {
        setValues({ repeatType: RepeatType.NONE })
        setError({});
        onClose()
    }

    const onTextChange = (value, name) => {
        let newValues = { ...values };

        // Cambia filtro -> reset valore filtro
        if (name === "filter") {
            delete newValues.filterValue;
        }

        // Cambia repeatType -> reset campi dipendenti
        if (name === "repeatType") {
            newValues = applyRepeatTypeSelection(newValues, value);
        } else {
            // Applica il nuovo valore del campo modificato
            newValues[name] = value;
        }

        setError({});

        setValues(newValues);
    };

    return (
        <BaseModal open={open} onClose={close} onSave={save}>
            <Grid container spacing={4} style={{ margin: 0, width: "100%", marginBottom: "1rem" }}
                justifyContent={"center"}>

                <TextInputContainer xs={12} label={t('title')} value={values.title}
                    type="string" required
                    onTextChange={(value) => {
                        onTextChange(value, "title")
                    }}
                    error={error.title} />

                <Grid item xs={12} style={{ padding: "16px" }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" className={classes.radioLabel}
                            sx={{ color: "primary.light", '&.Mui-focused': { color: "primary.light" } }}>
                            {t('repetition')}
                        </FormLabel>
                        <RadioGroup row value={values.repeatType}
                            onChange={(event) => onTextChange(event.target.value, "repeatType")}
                            className={classes.radioLabel}>
                            <FormControlLabel value={RepeatType.NONE} control={<Radio
                                sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                                label={t('none')} />
                            <FormControlLabel value={RepeatType.MONTH} control={<Radio
                                sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                                label={t('monthly')} />
                            <FormControlLabel value={RepeatType.CUSTOM} control={<Radio
                                sx={{ color: "primary.light", '&.Mui-checked': { color: "secondary.main" } }} />}
                                label={t('custom')} />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                {
                    (values.repeatType === RepeatType.NONE || values.repeatType === RepeatType.CUSTOM) &&
                    <TextInputContainer xs={12} required label={t('startDate')} value={values.startDate}
                        type="date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onTextChange={(value) => {
                            onTextChange(value, "startDate")
                        }}
                        error={error.startDate} />
                }
                {
                    (values.repeatType === RepeatType.NONE || values.repeatType === RepeatType.CUSTOM) &&
                    <TextInputContainer xs={12} required label={t('endDate')} value={values.endDate}
                        type="date" min={values.startDate}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onTextChange={(value) => {
                            onTextChange(value, "endDate")
                        }}
                        error={error.endDate} />
                }
                {values.repeatType === RepeatType.MONTH &&
                    <DatePicker
                        fullWidth
                        inputVariant="outlined"
                        classes={{ root: classes.root }}
                        inputProps={{ className: classes.input }}
                        views={["year", "month"]}
                        label={t('startMonth')}
                        value={values.startMonth}
                        onChange={(value) => onTextChange(value, "startMonth")}
                        renderInput={(params) => <TextInputContainer xs={12} {...params} helperText={null} />} />
                }
                {
                    (values.repeatType === RepeatType.MONTH || values.repeatType === RepeatType.CUSTOM) &&
                    <TextInputContainer xs={12} required label={t('repeatNum')} value={values.repeatNum}
                        type="number"
                        min={1} step={1}
                        onTextChange={(value) => {
                            onTextChange(value, "repeatNum")
                        }}
                        error={error.repeatNum} />
                }
                {

                    (values.repeatType === RepeatType.MONTH || values.repeatType === RepeatType.CUSTOM) 
                    &&
                    (
                        <>
                            <Grid item xs={12} style={{ padding: "16px" }}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Switch color="secondary" />}
                                        label={t('isAwardLimit')}
                                        onChange={(e) => onTextChange(!values.isAwardLimit, "isAwardLimit")}
                                    />
                                </FormGroup>
                                {values.isAwardLimit &&
                                    <TextInputContainer xs={12} required label={t('awardLimit')} value={values.awardsLimitForGroup}
                                        type="number"
                                        min={0} step={1}
                                        startAdornment={<Info>{t('awardsValueLimitForGroupInfo')}</Info>}
                                        onTextChange={(value) => {
                                            onTextChange(value, "awardsLimitForGroup")
                                        }}
                                        error={error.awardsLimitForGroup} />
                                }
                            </Grid>
                            <Grid item xs={12} style={{ padding: "16px" }}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Switch color="secondary" />}
                                        label={t('isAwardsValueLimitForGroup')}
                                        onChange={(e) => onTextChange(!values.isAwardsValueLimitForGroup, "isAwardsValueLimitForGroup")}
                                    />
                                </FormGroup>
                                {values.isAwardsValueLimitForGroup &&
                                    <TextInputContainer xs={12} required label={t('awardsValueLimitForGroup')} value={values.awardsValueLimitForGroup}
                                        type="number"
                                        min={0} step={1}
                                        startAdornment={<Info>{t('awardsValueLimitForGroupInfo')}</Info>}
                                        onTextChange={(value) => {
                                            onTextChange(value, "awardsValueLimitForGroup")
                                        }}
                                        error={error.awardsValueLimitForGroup} />
                                }
                            </Grid>
                        </>
                    )
                }

                <TextInputContainer xs={12} required label={t('value')} value={values.value}
                    options={usefulValues} select
                    onTextChange={(value) => {
                        onTextChange(value, "value")
                    }}
                    error={error.value}>
                    {usefulValues.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextInputContainer>

                <TextInputContainer xs={12} label={t('filterCategory')} value={values.filter}
                    options={rankingFilter} select
                    onTextChange={(value) => {
                        onTextChange(value, "filter")
                    }}
                    error={error.filter}>
                    <MenuItem key='' value=''>
                        <br />
                    </MenuItem>
                    {rankingFilter.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextInputContainer>
                {
                    values.filter !== null && values.filter === 0 &&
                    <SessionTypeSelect xs={12} label={t('valueFilter')} value={values.filterValue}
                        onTextChange={(value) => {
                            onTextChange(value, "filterValue")
                        }}
                        error={error.filterValue} />
                }
                {
                    values.filter !== null && values.filter === 1 &&
                    <>
                        <TextInputContainer xs={6} required label={t('minAge')} value={values.minAge}
                            type="number"
                            min={1} step={1}
                            onTextChange={(value) => {
                                onTextChange(value, "minAge")
                            }}
                            error={error.minAge} />

                        <TextInputContainer xs={6} required label={t('maxAge')} value={values.maxAge}
                            type="number"
                            min={1} step={1}
                            onTextChange={(value) => {
                                onTextChange(value, "maxAge")
                            }}
                            error={error.maxAge} />

                    </>
                }
                {
                    values.filter !== null && values.filter === 2 &&
                    <GenderSelect
                        xs={12} required value={values.filterValue}
                        error={error.filterValue}
                        onTextChange={(value) => {
                            onTextChange(value, "filterValue")
                        }}
                    />
                }
                {
                    values.filter !== null && values.filter === 3 &&
                    <TextInputContainer xs={12} required label={t('valueFilter')} value={values.filterValue}
                        options={customField} select
                        onTextChange={(value) => {
                            onTextChange(value, "filterValue")
                        }}
                        error={error.filterValue}>
                        {customField.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                    </TextInputContainer>
                }
                {
                    values.filter !== null && values.filter === 4 &&
                    <>
                        <SessionTypeSelect xs={6} label={t('sessionType')} value={values.minAge}
                            onTextChange={(value) => {
                                onTextChange(value, "minAge")
                            }}
                            error={error.minAge} />

                        <TextInputContainer xs={6} required label={t('customField')} value={values.maxAge}
                            options={customField} select
                            onTextChange={(value) => {
                                onTextChange(value, "maxAge")
                            }}
                            error={error.maxAge}>
                            {customField.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                        </TextInputContainer>

                    </>
                }
                <Grid item xs={8} className={classes.previewTitle}>
                    {t('ranksPreview')}:
                </Grid>
                <Grid item xs={8} className={classes.preview}>
                    {rankPreview}
                </Grid>
            </Grid>
        </BaseModal>
    );
}
